import React from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '../ui/SEOHead';
import PageHero from '../ui/PageHero';
import useScrollReveal from '../../hooks/useScrollReveal';
import { PATIENT_CENTER_DATA } from '../../data/navigation';
import GoogleReviews from '../ui/GoogleReviews';
import OfficeTour from '../ui/OfficeTour';
import SmileGallery from '../ui/SmileGallery';
import NewPatientForms from '../ui/NewPatientForms';
import s from './PageContent.module.css';

export default function PatientCenterPage() {
  useScrollReveal();
  const { section } = useParams();
  const active = section && PATIENT_CENTER_DATA[section] ? PATIENT_CENTER_DATA[section] : null;

  return (
    <>
      <SEOHead
        title={active ? active.title + ' | Celebrity Smile Studio' : 'Patient Center | Celebrity Smile Studio'}
        description="Patient resources at Celebrity Smile Studio — financing, office policies, common questions, emergency care, and more."
        canonical={section ? '/patient-center/' + section : '/patient-center'}
      />
      <PageHero
        eyebrow="Patient Resources"
        title={active ? active.title : 'Patient <em>Center</em>'}
        subtitle="Everything you need to make your visit smooth and comfortable."
        breadcrumbs={active
          ? [{ label: 'Patient Center', path: '/patient-center' }, { label: active.title }]
          : [{ label: 'Patient Center' }]
        }
        image="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=1600&q=80"
      />

      <section className="section">
        <div className="container">
          <div className={s.innerLayout}>

            {/* Sidebar nav */}
            <div className={s.sidebar}>
              <p className={s.pcNavHead}>Navigation</p>
              {Object.entries(PATIENT_CENTER_DATA).map(([slug, info]) => (
                <Link
                  key={slug}
                  to={'/patient-center/' + slug}
                  className={s.pcNavLink + (section === slug ? ' ' + s.pcNavLinkActive : '')}
                >
                  <span className={s.pcNavIcon}>{info.icon}</span>
                  {info.title}
                </Link>
              ))}
              <div className={s.pcHelpBox}>
                <div className={s.pcHelpLabel}>Need help?</div>
                <a href="tel:2152335811" className={s.pcHelpPhone}>(215) 233-5811</a>
              </div>
            </div>

            {/* Main content */}
            <div className="reveal">
              {active ? (
                <>
                  <div className={s.sectionIcon}>{active.icon}</div>
                  {section === 'testimonials' ? (
                    <GoogleReviews apiKey={process.env.REACT_APP_GOOGLE_PLACES_API_KEY || 'YOUR_API_KEY'} />
                  ) : section === 'office-tour' ? (
                    <>
                      <div className={s.richContent} dangerouslySetInnerHTML={{ __html: active.content }} />
                      <OfficeTour />
                    </>
                  ) : section === 'smile-gallery' ? (
                    <SmileGallery />
                  ) : section === 'new-patient-forms' ? (
                    <NewPatientForms />
                  ) : (
                    <div className={s.richContent} dangerouslySetInnerHTML={{ __html: active.content }} />
                  )}
                  <div className={s.ctaWrap}>
                    <Link to="/book-appointment" className="btn btn-primary">Schedule an Appointment</Link>
                  </div>
                </>
              ) : (
                <>
                  <div className={s.richContent}>
                    <h2>Patient Center</h2>
                    <p>Select a topic from the menu to learn more, or contact us directly — we're happy to help with any questions.</p>
                  </div>
                  <div className={s.topicGrid}>
                    {Object.entries(PATIENT_CENTER_DATA).map(([slug, info]) => (
                      <Link key={slug} to={'/patient-center/' + slug} className={s.topicCard}>
                        <div className={s.topicIcon}>{info.icon}</div>
                        <div className={s.topicTitle}>{info.title}</div>
                        <span className={s.topicArrow}>Learn more →</span>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
